// Arcade (render 03 for layout; WI-13): the trip's reward machine. A twist is the reward: it spends
// a play and opens straight onto one collectible, act key or real prize (useArcade `twist`, the
// reveal overlay). No card round: the card sets live in Learn (WI-10). With the world stage live,
// the machine is its 3D cabinet on the stage (this screen leaves a slot for it and drives it
// through the bridge); in Calm/Still or without WebGL it's the machine's flat one (B16).
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { Person } from '../../shared/events'
import type { Pack } from '../../shared/pack/schema'
import { goTab, openOverlay } from '../app/router'
import { useToast } from '../app/toast'
import { useRewardMachine } from '../arcade/machines'
import { useGrownUpName } from '../arcade/PlaysSheet'
import { islandTitle } from '../arcade/Reward'
import { twist, useArcade, useRewardWorld } from '../arcade/useArcade'
import { kvGet, kvSet } from '../data/db'
import { useSettings } from '../data/settings'
import { stageOn } from '../domain/release'
import { playersOf } from '../domain/players'
import { myPrizes } from '../domain/rewards'
import { ownedCollectibles } from '../domain/release'
import { tasksFor } from '../domain/tasks'
import { useSession } from '../data/session'
import type { CardSetTheme } from '../domain/arcade/types'
import { useMotionTier } from '../motion/tier'
import { useStage } from '../stage/bridge'
import { useCrateView } from './crateView'
import { t } from '../strings/en'

const THEME: CardSetTheme = { glyph: '★', jpGlyph: false, color: 'red' }

export function ArcadeScreen({ pack, person }: { pack: Pack; person: Person }) {
  const { plays, progress, events, player, rules, prizes } = useArcade(pack, person.id)
  const { trip, world, day } = useRewardWorld(pack, person.id)
  // Bonus plays go to someone else who plays (none for a solo traveller).
  const people = useSession((s) => s.people)
  const othersPlay = playersOf(people ?? []).some((p) => p.id !== person.id)
  const tier = useMotionTier()
  const organiserTools = useSettings((s) => s.organiserTools)
  const showToast = useToast((s) => s.show)
  const grownUp = useGrownUpName()
  const machine = useRewardMachine()
  // The cabinet's plate: the island whose set the twists fill now.
  const title = islandTitle(stageOn(trip, day)?.place ?? '') || t.arcade.title
  const p = plays
  const waitingPrizes = useMemo(() => (player ? myPrizes(events, player).waiting.length : 0), [events, player])
  const collected = useMemo(() => [...ownedCollectibles(events, person.id).keys()].length, [events, person.id])
  const tasksToDo = useMemo(() => (player && rules.tasks?.on !== false ? tasksFor(events, player, world.phase).filter((x) => x.state === 'todo').length : 0), [events, player, rules, world.phase])
  // WI-34: 3D in every motion tier (Calm and Still get a still frame: arcadeSet.setTier).
  const stage3d = useStage((s) => s.status === 'live')
  const [busy, setBusy] = useState(false)
  const slot = useRef<HTMLDivElement>(null)
  const locked = p.left <= 0

  // "Bonus from Dad!" once per new grant.
  useEffect(() => {
    if (!progress.lastBonusAt) return
    void kvGet<number>('bonusSeenAt').then((seen) => {
      if ((seen ?? 0) >= progress.lastBonusAt) return
      const n = plays.lines.filter((l) => l.trigger === 'bonus').reduce((s, l) => s + l.plays, 0)
      if (n) showToast(t.arcade.bonusNote(grownUp, n), 3000)
      void kvSet('bonusSeenAt', progress.lastBonusAt)
    })
  }, [progress.lastBonusAt, plays, showToast, grownUp])

  // The machine's spend moment is over: pay the twist and open the reveal.
  const onDropped = async () => {
    const done = await twist(pack, person.id, { plays, events, rules, prizes })
    if (done) openOverlay('reward')
  }
  const dropRef = useRef(onDropped)
  useEffect(() => {
    dropRef.current = onDropped
  })

  // The 3D machine: what it looks like, where it sits, and what its twist does.
  // Registered for as long as this screen is up, so a twist that started in 3D still opens its
  // reveal if the tier drops to the flat machine mid-sequence.
  useEffect(() => {
    useStage.getState().set({ arcadeEvents: { onDropped: () => void dropRef.current(), onBusy: setBusy } })
    return () => useStage.getState().set({ arcadeEvents: { onDropped: () => {}, onBusy: () => {} } })
  }, [])
  useLayoutEffect(() => {
    if (!stage3d) return
    const measure = () => {
      const r = slot.current?.getBoundingClientRect()
      if (!r) return
      useStage.getState().set({ arcade: { machine: machine.id, title, colour: THEME.color, locked, frame: { top: r.top, bottom: r.bottom }, toys: [], plank: null, rack: null } })
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (slot.current) ro.observe(slot.current)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [stage3d, machine.id, title, locked])

  return (
    <main className={`screen arcade${stage3d ? ' on-stage' : ''}`}>
      <div className="screen-head">
        <h1 className="h1">{t.arcade.title}</h1>
        <span className="head-pills">
          {player ? (
            <button className="pill press" style={{ color: 'var(--ink)' }} aria-label={t.arcade.playsLabel(p.left)} onClick={() => openOverlay('plays')}>
              {t.arcade.playsToday(p.left)}
            </button>
          ) : null}
          {player ? (
            <button className="pill press" aria-label={t.arcade.myPrizesLabel(waitingPrizes)} onClick={() => openOverlay('my-prizes')}>
              {t.arcade.myPrizes(waitingPrizes)}
            </button>
          ) : null}
        </span>
      </div>

      {stage3d ? (
        <div className="gashapon gashapon-3d">
          <div ref={slot} className="machine-slot" role="img" aria-label={t.arcade.machineLabel(title, machine.words, locked)} />
          <button className="btn y press twist-btn" disabled={locked || busy} hidden={locked} onClick={() => useStage.getState().set({ twist: useStage.getState().twist + 1 })}>
            {machine.words.action}
          </button>
        </div>
      ) : (
        <machine.Flat title={title} theme={THEME} locked={locked} tier={tier} label={machine.words.action} onDropped={() => void onDropped()} />
      )}
      {/* What a twist holds, before one is spent (WI-11, WI-13). */}
      <p className="caption machine-blurb">{t.arcade.whatsInside(machine.words)}</p>
      {!player ? (
        <div className="panel sheet-panel not-playing">
          <div className="row">
            <div>
              <b>{t.arcade.notPlaying}</b>
              <small>{person.kind === 'admin' ? t.arcade.notPlayingAdmin : t.arcade.notPlayingAdult}</small>
            </div>
          </div>
        </div>
      ) : locked ? (
        <button className="btn off locked-btn press" onClick={() => openOverlay('plays')}>
          {t.arcade.noPlays}
          <small>{t.arcade.earnMore}</small>
        </button>
      ) : null}

      {player ? (
        <div className="arcade-links">
          <button
            className="panel arcade-link press"
            onClick={() => {
              useCrateView.setState({ view: 'shelf' })
              goTab('crate')
            }}
          >
            <span aria-hidden>🦊</span>
            <b>{t.arcade.shelf(collected)}</b>
            <small>{t.arcade.actsOpen(world.unlocked.size)}</small>
          </button>
          {tasksToDo ? (
            <button className="panel arcade-link press" onClick={() => openOverlay('plays')}>
              <span aria-hidden>🧳</span>
              <b>{t.arcade.tasks(tasksToDo)}</b>
              <small>{t.arcade.tasksSub}</small>
            </button>
          ) : null}
        </div>
      ) : null}

      {person.role === 'organiser' && organiserTools && othersPlay ? (
        <button className="btn w press bonus-btn" onClick={() => openOverlay('bonus')}>
          {t.arcade.bonusButton}
        </button>
      ) : null}
    </main>
  )
}
